import { Link } from 'react-router';
import { Button } from '../components/ui/button';
import { Check, ArrowRight, Clock, Users, TrendingUp, Zap, Sparkles, Star, Play, Award, Shield, AlertCircle, Building2, BarChart3, CheckCircle2, PhoneCall } from 'lucide-react';
import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { TestimonialsSection } from '../components/sections/TestimonialsSection';
import { BundleSelectorWithGuide } from '../components/bundles/BundleSelectorWithGuide';
import { TabbedPricing } from '../components/bundles/TabbedPricing';
import { SmartInvestmentSection } from '../components/bundles/SmartInvestmentSection';
import { getApiUrl, getApiHeaders } from '../utils/apiConfig';
import { Helmet } from 'react-helmet-async';

// Lazy load — only needed on interaction / mouse-leave
const BundleContactModal = lazy(() => import('../components/BundleContactModal').then(m => ({ default: m.BundleContactModal })));
const ExitIntentPopup    = lazy(() => import('../components/bundles/ExitIntentPopup').then(m => ({ default: m.ExitIntentPopup })));

const API_URL = getApiUrl();

// ============================================
// ICON MAP
// ============================================

const iconMap: { [key: string]: any } = {
  Star, Users, Award, Sparkles, Zap, TrendingUp, AlertCircle,
};

// ============================================
// INTERFACES
// ============================================

interface SocialProofBadge {
  icon: string;
  label: string;
  value: string;
  type: 'success' | 'urgent' | 'premium';
  visible: boolean;
  animated?: boolean;
  showStarRating?: boolean;
}

interface BundlesHeroData {
  badge: string;
  titlePart1: string;
  titlePart2: string;
  discountPercentage: string;
  description: string;
  primaryCTA: { text: string; link: string; visible: boolean; };
  secondaryCTA: { text: string; link: string; visible: boolean; };
  spotsLeft: number;
  socialProofBadges: SocialProofBadge[];
  visible: boolean;
}

// ============================================
// MODULE-LEVEL CONSTANTS
// ============================================

const FALLBACK_3D = [
  {
    name: 'Starter', videos: 5, price: 7500, pricePerVideo: 1500,
    regularPrice: 10000, savings: 2500, savingsPercent: 25, popular: false,
    features: [
      '5 high-impact 3D videos', '60-90 second duration each',
      'Perfect for product launches', '2 revision rounds per video',
      'Multiple format exports', 'Commercial usage rights',
    ],
  },
  {
    name: 'Growth', videos: 10, price: 13500, pricePerVideo: 1350,
    regularPrice: 20000, savings: 6500, savingsPercent: 33, popular: true,
    features: [
      '10 professional 3D videos', '60-90 second duration each',
      'Ideal for full product lines', '3 revision rounds per video',
      'Priority production queue', 'Multiple format exports',
      'Commercial usage rights', 'Dedicated project manager',
    ],
  },
  {
    name: 'Impact', videos: 20, price: 20000, pricePerVideo: 1000,
    regularPrice: 40000, savings: 20000, savingsPercent: 50, popular: false,
    features: [
      '20 premium 3D videos', '60-90 second duration each',
      'Complete video library', 'Unlimited revisions',
      'Priority production queue', 'Multiple format exports',
      'Commercial usage rights', 'Dedicated project manager',
      'Strategic consultation calls', 'Custom branding package',
    ],
  },
];

const FALLBACK_2D = [
  {
    name: 'Starter', videos: 5, price: 3500, pricePerVideo: 700,
    regularPrice: 5000, savings: 1500, savingsPercent: 30, popular: false,
    features: [
      '5 engaging 2D videos', '45-60 second duration each',
      'Great for social content', '2 revision rounds per video',
      'Multiple format exports', 'Commercial usage rights',
    ],
  },
  {
    name: 'Growth', videos: 10, price: 6500, pricePerVideo: 650,
    regularPrice: 10000, savings: 3500, savingsPercent: 35, popular: true,
    features: [
      '10 professional 2D videos', '45-60 second duration each',
      'Perfect for internal comms', '3 revision rounds per video',
      'Priority production queue', 'Multiple format exports',
      'Commercial usage rights', 'Dedicated project manager',
    ],
  },
  {
    name: 'Impact', videos: 20, price: 10000, pricePerVideo: 500,
    regularPrice: 20000, savings: 10000, savingsPercent: 50, popular: false,
    features: [
      '20 premium 2D videos', '45-60 second duration each',
      'Complete explainer library', 'Unlimited revisions',
      'Priority production queue', 'Multiple format exports',
      'Commercial usage rights', 'Dedicated project manager',
      'Strategic consultation calls', 'Social media optimization',
    ],
  },
];

const DEFAULT_SECTION_HEADER = {
  badge: 'Premium Quality',
  title: 'Choose Your Bundle',
  subtitle: 'Select between 3D or 2D animation styles, then choose the package size that fits your needs',
  tab3DLabel: '3D BUNDLES',
  tab2DLabel: '2D BUNDLES',
  description3D: 'Best for high-impact product demos, training, and marketing visuals that showcase complex products in stunning detail',
  description2D: 'Perfect for explainer videos, social media content, and internal communications that need quick turnaround',
  showMostPopular: true,
};

const DEFAULT_SMART_INVESTMENT = {
  badge: 'Smart Investment',
  title: 'Why Smart Companies Choose Bundles',
  subtitle: 'Strategic video bundles deliver measurable ROI with significant cost savings and operational advantages',
  benefits: [
    { icon: 'TrendingUp', gradientFrom: '#10b981', gradientTo: '#059669', title: 'Massive Savings', description: 'Save up to 50% vs. individual video pricing', visible: true },
    { icon: 'Clock', gradientFrom: '#D74444', gradientTo: '#E87D3E', title: 'Faster Delivery', description: 'Priority queue means your videos are completed faster', visible: true },
    { icon: 'Award', gradientFrom: '#2F2E5C', gradientTo: '#4a487a', title: 'Consistent Quality', description: 'Unified style and messaging across all videos', visible: true },
    { icon: 'Shield', gradientFrom: '#E87D3E', gradientTo: '#E8B84D', title: 'Budget Protection', description: 'Lock in rates before 2026 price increase', visible: true },
  ],
  visible: true,
};

const DEFAULT_TRUST_BADGES = {
  statCards: [
    { value: '280+', label: 'Happy Clients', gradientFrom: '#E87D3E', gradientTo: '#E8B84D', showStars: false, visible: true },
    { value: '650+', label: 'Videos Delivered', gradientFrom: '#D74444', gradientTo: '#E87D3E', showStars: false, visible: true },
    { value: '5.0', label: 'Client Rating', gradientFrom: '#E8B84D', gradientTo: '#E87D3E', showStars: true, visible: true },
  ],
  infoBox: {
    icon: '💡',
    title: 'Not Sure Which Bundle Is Right for You?',
    description: "Schedule a free consultation call and we'll help you choose the perfect package based on your goals and budget.",
    visible: true,
  },
  trustBadges: [
    { icon: 'Shield', text: 'Money-Back Guarantee', gradientFrom: '#10b981', gradientTo: '#059669', visible: true },
    { icon: 'Clock', text: 'Priority Delivery', gradientFrom: '#E87D3E', gradientTo: '#E8B84D', visible: true },
    { icon: 'Award', text: 'Industry Experts', gradientFrom: '#2F2E5C', gradientTo: '#4a487a', visible: true },
  ],
  visible: true,
};

const DEFAULT_EXIT_INTENT = {
  badge: { icon: '⏰', gradientFrom: '#E87D3E', gradientTo: '#E8B84D' },
  headline: 'Wait! Before You Go...',
  description: 'Not ready to commit to a bundle yet? No problem!',
  offerCard: {
    icon: 'Calendar', iconGradientFrom: '#3b82f6', iconGradientTo: '#06b6d4',
    title: 'Get a FREE Video Strategy Session',
    bulletPoints: [
      { text: '30-min consultation call', visible: true },
      { text: 'Custom video roadmap for your business', visible: true },
      { text: 'Personalized bundle recommendation', visible: true },
      { text: 'ROI projection (no fluff, real numbers)', visible: true },
    ],
  },
  ctaBookSession: { text: 'Book Free Session', link: '/contact', icon: 'Calendar', visible: true },
  ctaNoThanks: { text: "No Thanks, I'll Browse", visible: true },
  downloadGuide: { preText: 'Or download our free guide:', linkText: "The B2B Video Bundle Buyer's Guide (PDF)", pdfUrl: '', visible: true },
  visible: true,
};

// ============================================
// TimeUnit — module-level
// ============================================

function TimeUnit({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center">
      <div className="bg-white rounded-xl shadow-lg border-2 border-[#D74444]/20 px-4 py-3 sm:px-6 sm:py-4 min-w-[70px] sm:min-w-[90px]">
        <div className="text-2xl sm:text-4xl font-bold text-[#D74444]">
          {value.toString().padStart(2, '0')}
        </div>
      </div>
      <div className="text-xs sm:text-sm text-gray-600 mt-2 font-medium">{label}</div>
    </div>
  );
}

// ============================================
// PRICING SKELETON — original
// ============================================

function PricingSkeleton() {
  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-8">
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="h-8 w-36 bg-gray-200 rounded-full relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>
          <div className="h-9 bg-gray-200 rounded w-72 mx-auto relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
          <div className="h-4 bg-gray-200 rounded w-96 mx-auto relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="flex justify-center gap-4">
          <div className="h-11 w-40 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
          <div className="h-11 w-40 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-6">
          {[0, 1, 2].map(i => (
            <div key={i} className="bg-white rounded-2xl border-2 border-gray-200 p-6 space-y-4">
              <div className="h-6 w-24 bg-gray-200 rounded relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              <div className="h-12 w-32 bg-gray-200 rounded relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              {[1, 2, 3, 4].map(j => (
                <div key={j} className="h-4 bg-gray-100 rounded relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
              ))}
              <div className="h-11 bg-gray-200 rounded-full relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================
// COMPONENT
// ============================================

export function BundlesPage() {
  const [heroData, setHeroData]                       = useState<BundlesHeroData | null>(null);
  const [heroLoading, setHeroLoading]                 = useState(true);
  const [threeDBundle, setThreeDBundle]               = useState<any[]>(FALLBACK_3D);
  const [twoDBundle, setTwoDBundle]                   = useState<any[]>(FALLBACK_2D);
  const [pricingLoading, setPricingLoading]           = useState(true);
  const [sectionHeader, setSectionHeader]             = useState(DEFAULT_SECTION_HEADER);
  const [smartInvestmentData, setSmartInvestmentData] = useState(DEFAULT_SMART_INVESTMENT);
  const [trustBadgesData, setTrustBadgesData]         = useState(DEFAULT_TRUST_BADGES);
  const [exitIntentData, setExitIntentData]           = useState(DEFAULT_EXIT_INTENT);

  const [isModalOpen, setIsModalOpen]       = useState(false);
  const [selectedBundle, setSelectedBundle] = useState<{
    type: '3D' | '2D';
    tier: 'Starter' | 'Growth' | 'Impact';
    price: number;
    videos: number;
  } | null>(null);

  const [showExitIntent, setShowExitIntent]         = useState(false);
  const [hasShownExitIntent, setHasShownExitIntent] = useState(false);

  const headers = getApiHeaders();

  // ============================================
  // FETCH FUNCTIONS — UNCHANGED
  // ============================================

  const fetchHeroData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/bundles/hero?t=${Date.now()}`, { headers });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) setHeroData(result.data);
      }
    } catch {
      // silently fall through to null
    } finally {
      setHeroLoading(false);
    }
  }, []);

  const fetchAllSections = useCallback(async () => {
    try {
      const [res3D, res2D, resHeader, resInvestment, resTrust, resExit] = await Promise.all([
        fetch(`${API_URL}/bundles/pricing-3d?t=${Date.now()}`, { headers }),
        fetch(`${API_URL}/bundles/pricing-2d?t=${Date.now()}`, { headers }),
        fetch(`${API_URL}/bundles/pricing-header?t=${Date.now()}`, { headers }),
        fetch(`${API_URL}/bundles/why-choose?t=${Date.now()}`, { headers }),
        fetch(`${API_URL}/bundles/trust-badges?t=${Date.now()}`, { headers }),
        fetch(`${API_URL}/bundles/exit-intent?t=${Date.now()}`, { headers }),
      ]);

      if (res3D.ok) {
        const r = await res3D.json();
        if (r.success && Array.isArray(r.data) && r.data.length > 0) setThreeDBundle(r.data);
      }
      if (res2D.ok) {
        const r = await res2D.json();
        if (r.success && Array.isArray(r.data) && r.data.length > 0) setTwoDBundle(r.data);
      }
      if (resHeader.ok) {
        const r = await resHeader.json();
        if (r.success && r.data) setSectionHeader(r.data);
      }
      if (resInvestment.ok) {
        const r = await resInvestment.json();
        if (r.success && r.data) setSmartInvestmentData(r.data);
      }
      if (resTrust.ok) {
        const r = await resTrust.json();
        if (r.success && r.data) setTrustBadgesData(r.data);
      }
      if (resExit.ok) {
        const r = await resExit.json();
        if (r.success && r.data) setExitIntentData(r.data);
      }
    } catch {
      // silently fall through
    } finally {
      setPricingLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHeroData();
    fetchAllSections();
  }, [fetchHeroData, fetchAllSections]);

  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0 && !hasShownExitIntent) {
        setShowExitIntent(true);
        setHasShownExitIntent(true);
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [hasShownExitIntent]);

  // ============================================
  // HANDLERS — UNCHANGED
  // ============================================

  const handleClaimBundle = (
    type: '3D' | '2D',
    tier: 'Starter' | 'Growth' | 'Impact',
    price: number,
    videos: number
  ) => {
    setSelectedBundle({ type, tier, price, videos });
    setIsModalOpen(true);
  };

  const handleSelectorRecommendation = (
    _type: '3D' | '2D',
    _tier: 'Starter' | 'Growth' | 'Impact'
  ) => {
    document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <>
      <Helmet>
        <title>Animation Video Bundles | Save Up to 50% | Creabiz Studio</title>
        <meta name="description" content="Save up to 50% with Creabiz Studio's animation video bundles. Choose from 3D or 2D animation packages starting at 5 videos. Trusted by 280+ B2B companies." />
        <meta property="og:title" content="Animation Video Bundles | Save Up to 50% | Creabiz Studio" />
        <meta property="og:description" content="Save up to 50% with our animation video bundles. 3D and 2D options available. Trusted by 280+ B2B companies worldwide." />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="Animation Video Bundles | Creabiz Studio" />
        <meta property="twitter:description" content="Save up to 50% with our animation video bundles. 3D and 2D options available." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">

        {/* ── Hero ── */}
        {heroLoading ? (
          /* LOADING SKELETON — original exactly */
          <section className="relative min-h-[650px] flex items-center px-4 sm:px-6 lg:px-8 py-16 sm:py-24 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#2F2E5C] via-[#1a1935] to-[#2F2E5C]" />
            <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-gradient-to-br from-[#E8B84D]/25 via-[#E87D3E]/15 to-transparent rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#D74444]/25 via-[#e85e5e]/15 to-transparent rounded-full blur-3xl" />
            <div className="max-w-5xl mx-auto relative z-10 w-full space-y-6">
              <div className="flex justify-center">
                <div className="h-12 w-64 bg-white/10 rounded-full relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
              </div>
              <div className="space-y-4">
                <div className="h-16 bg-white/10 rounded-2xl mx-auto max-w-4xl relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
                <div className="h-16 bg-white/10 rounded-2xl mx-auto max-w-3xl relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
              </div>
              <div className="space-y-3">
                <div className="h-6 bg-white/10 rounded-xl mx-auto max-w-2xl relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
              </div>
              <div className="flex justify-center">
                <div className="h-14 w-72 bg-white/10 rounded-2xl relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-20 w-64 bg-white/10 rounded-2xl relative overflow-hidden">
                    <div className="absolute inset-0 shimmer" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : heroData?.visible ? (
          /* HERO — original structure preserved, B2B additions inserted cleanly */
          <section className="relative min-h-[650px] flex items-center px-4 sm:px-6 lg:px-8 py-16 sm:py-24 overflow-hidden">
            {/* Original backgrounds — untouched */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#2F2E5C] via-[#1a1935] to-[#2F2E5C]" />
            <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-gradient-to-br from-[#E8B84D]/25 via-[#E87D3E]/15 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '4s' }} />
            <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-[#D74444]/25 via-[#e85e5e]/15 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '5s' }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-br from-white/5 to-transparent rounded-full blur-3xl" />
            <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />

            <div className="max-w-5xl mx-auto relative z-10 text-white w-full">

              {/* ── NEW: KPI strip — B2B trust signals at top ── */}
              <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
                {[
                  { icon: <Building2 className="w-4 h-4" />, value: '280+', label: 'B2B Companies' },
                  { icon: <BarChart3 className="w-4 h-4" />, value: '650+', label: 'Videos Delivered' },
                  { icon: <Star className="w-3.5 h-3.5 fill-[#E8B84D] text-[#E8B84D]" />, value: '5.0', label: 'Avg. Rating' },
                ].map((kpi, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm"
                  >
                    <span className="text-[#E8B84D]">{kpi.icon}</span>
                    <span className="font-bold text-sm text-white">{kpi.value}</span>
                    <span className="text-white/50 text-xs">{kpi.label}</span>
                  </div>
                ))}
                {/* CMS scarcity badge */}
                {heroData.spotsLeft > 0 && (
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#D74444]/20 border border-[#D74444]/40 backdrop-blur-sm">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D74444] opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D74444]" />
                    </span>
                    <span className="text-[#ff9090] font-semibold text-sm">
                      Only {heroData.spotsLeft} spots left
                    </span>
                  </div>
                )}
              </div>

              {/* Badge — original */}
              <div className="flex justify-center mb-8">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] rounded-full blur-xl opacity-50 group-hover:opacity-75 transition-opacity" />
                  <div className="relative inline-flex items-center gap-3 bg-white/20 backdrop-blur-xl px-6 py-3.5 rounded-full border border-white/40 shadow-2xl">
                    <Zap className="w-5 h-5 text-[#E8B84D] fill-[#E8B84D] animate-pulse" />
                    <span className="font-bold text-white text-sm tracking-widest">{heroData.badge}</span>
                    <Zap className="w-5 h-5 text-[#E8B84D] fill-[#E8B84D] animate-pulse" />
                  </div>
                </div>
              </div>

              {/* Headline — original */}
              <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-center mb-6 leading-tight">
                {heroData.titlePart1}{' '}
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-[#E87D3E] via-[#E8B84D] to-[#E87D3E] bg-clip-text text-transparent">
                    {heroData.titlePart2}
                  </span>
                  <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#E8B84D] to-transparent rounded-full" />
                </span>
              </h1>

              {/* Description — original */}
              <p className="text-lg sm:text-xl text-center text-white/90 mb-10 max-w-3xl mx-auto leading-relaxed">
                {' '}
                <span className="relative inline-flex items-center font-bold bg-white/20 backdrop-blur-sm px-4 py-1.5 rounded-xl border border-white/30 mx-1">
                  <TrendingUp className="w-4 h-4 mr-2 text-[#E8B84D]" />
                  {heroData.discountPercentage}
                </span>
                {' '}{heroData.description}
              </p>

              {/* ── NEW: Assurance strip — B2B procurement signals ── */}
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-8">
                {[
                  { icon: <CheckCircle2 className="w-4 h-4 text-emerald-400" />, text: 'NDA on request' },
                  { icon: <CheckCircle2 className="w-4 h-4 text-emerald-400" />, text: 'Dedicated project manager' },
                  { icon: <CheckCircle2 className="w-4 h-4 text-emerald-400" />, text: 'Industry Expertise' },
                ].map((item, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-white/60 text-sm">
                    {item.icon}
                    {item.text}
                  </span>
                ))}
              </div>

              {/* Primary CTA — original */}
              {heroData.primaryCTA.visible && (
                <div className="flex justify-center mb-10">
                  <a
                    href={heroData.primaryCTA.link}
                    className="group relative inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold px-8 py-4 rounded-2xl shadow-2xl transition-all hover:scale-105 hover:shadow-emerald-500/50"
                  >
                    <Users className="w-5 h-5" />
                    {heroData.primaryCTA.text}
                    <TrendingUp className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              )}

              {/* Social Proof Badges — original */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
                {heroData.socialProofBadges.filter(b => b.visible).map((badge, index) => {
                  const Icon = iconMap[badge.icon] || Users;
                  const gradientMap = {
                    success: 'from-emerald-500 to-emerald-600',
                    urgent: 'from-[#D74444] to-[#c13939]',
                    premium: 'from-[#E87D3E] to-[#E8B84D]',
                  };
                  const gradient = gradientMap[badge.type];
                  return (
                    <div
                      key={index}
                      className={`group relative ${badge.animated ? 'animate-pulse' : ''}`}
                      style={badge.animated ? { animationDuration: '2s' } : undefined}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-r ${gradient} rounded-2xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity`} />
                      <div className="relative flex items-center gap-3 bg-white/20 backdrop-blur-xl px-6 py-4 rounded-2xl border border-white/30 shadow-xl hover:bg-white/25 transition-all">
                        {badge.showStarRating ? (
                          <>
                            <div className="flex gap-0.5">
                              {[1, 2, 3, 4, 5].map(star => (
                                <Star key={star} className="w-4 h-4 fill-[#E8B84D] text-[#E8B84D]" />
                              ))}
                            </div>
                            <div>
                              <div className="text-xs text-white/70 uppercase tracking-wider font-medium">{badge.label}</div>
                              <div className="text-sm font-bold">{badge.value}</div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div className={`w-10 h-10 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center shadow-lg`}>
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <div className="text-xs text-white/70 uppercase tracking-wider font-medium">{badge.label}</div>
                              <div className="text-sm font-bold">
                                {badge.label === 'Limited Availability'
                                  ? `Only ${heroData.spotsLeft} spots left`
                                  : badge.value}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          </section>
        ) : null}

        {/* ── Bundle Selector — unchanged ── */}
        <BundleSelectorWithGuide onRecommendation={handleSelectorRecommendation} />

        {/* ── Tabbed Pricing — unchanged ── */}
        {pricingLoading ? (
          <PricingSkeleton />
        ) : (
          <TabbedPricing
            threeDBundle={threeDBundle}
            twoDBundle={twoDBundle}
            handleClaimBundle={handleClaimBundle}
            sectionHeader={sectionHeader}
          />
        )}

        {/* ── Smart Investment — unchanged ── */}
        <SmartInvestmentSection
          badge={smartInvestmentData.badge}
          title={smartInvestmentData.title}
          subtitle={smartInvestmentData.subtitle}
          benefits={smartInvestmentData.benefits}
          visible={smartInvestmentData.visible}
        />

        {/* ── Testimonials — unchanged ── */}
        <TestimonialsSection />

        {/* ── Stats & Trust — unchanged ── */}
        {trustBadgesData.visible && (
          <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-white overflow-hidden">
            <div className="absolute inset-0 opacity-[0.03]">
              <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-[#2F2E5C] to-transparent rounded-full blur-3xl" />
              <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-to-tl from-[#E87D3E] to-transparent rounded-full blur-3xl" />
            </div>
            <div className="max-w-5xl mx-auto relative z-10">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
                {trustBadgesData.statCards.filter(c => c.visible).map((card, index) => (
                  <div key={index} className="group relative">
                    <div
                      className="absolute inset-0 rounded-2xl blur-xl opacity-0 group-hover:opacity-40 transition-opacity"
                      style={{ background: `linear-gradient(to bottom right, ${card.gradientFrom}33, ${card.gradientTo}33)` }}
                    />
                    <div className="relative bg-gradient-to-br from-gray-50 to-white rounded-2xl border-2 border-gray-200 p-6 shadow-lg hover:shadow-xl transition-all">
                      <div
                        className="absolute inset-0 rounded-2xl opacity-5"
                        style={{ background: `linear-gradient(to bottom right, ${card.gradientFrom}, transparent)` }}
                      />
                      <div className="relative flex items-center justify-center gap-3">
                        <div className="text-center">
                          <div
                            className="text-4xl sm:text-5xl font-bold mb-2"
                            style={{
                              background: `linear-gradient(to right, ${card.gradientFrom}, ${card.gradientTo})`,
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                            }}
                          >
                            {card.value}
                          </div>
                          <div className="text-sm text-gray-600 font-medium">{card.label}</div>
                        </div>
                        {card.showStars && (
                          <div className="flex gap-0.5">
                            {[1, 2, 3, 4, 5].map(star => (
                              <Star key={star} className="w-5 h-5 fill-[#E8B84D] text-[#E8B84D]" />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {trustBadgesData.infoBox.visible && (
                <div className="relative group/info mb-12">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400/10 to-cyan-400/10 rounded-3xl blur-xl opacity-0 group-hover/info:opacity-60 transition-opacity" />
                  <div className="relative bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-blue-200/30 to-transparent rounded-full blur-2xl" />
                    <div className="relative flex items-start gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover/info:scale-110 transition-transform">
                        <span className="text-3xl">{trustBadgesData.infoBox.icon}</span>
                      </div>
                      <div className="text-left flex-1">
                        <div className="text-xl font-bold text-[#2F2E5C] mb-3">{trustBadgesData.infoBox.title}</div>
                        <p className="text-base text-gray-700 leading-relaxed">{trustBadgesData.infoBox.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap items-center justify-center gap-6">
                {trustBadgesData.trustBadges.filter(b => b.visible).map((badge, index, arr) => {
                  const IconComponent = badge.icon === 'Shield' ? Shield : badge.icon === 'Clock' ? Clock : Award;
                  return (
                    <div key={index} className="flex items-center gap-2">
                      <div className="flex items-center gap-2 text-gray-700">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md"
                          style={{ background: `linear-gradient(to bottom right, ${badge.gradientFrom}, ${badge.gradientTo})` }}
                        >
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-sm font-semibold">{badge.text}</span>
                      </div>
                      {index < arr.length - 1 && <div className="w-px h-8 bg-gray-300 ml-6" />}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* ── Modals — unchanged ── */}
        <Suspense fallback={null}>
          <BundleContactModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            bundleType={selectedBundle?.type}
            bundleTier={selectedBundle?.tier}
            bundlePrice={selectedBundle?.price}
            bundleVideos={selectedBundle?.videos}
          />
        </Suspense>

        <Suspense fallback={null}>
          <ExitIntentPopup
            isOpen={showExitIntent}
            onClose={() => setShowExitIntent(false)}
            data={exitIntentData}
          />
        </Suspense>
      </div>
    </>
  );
}